import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { QueueStatusLabelPipe } from '../../pipes/queue-status-label.pipe';
import { ExecutorGroupKey, QueueExecuterDto, QueueLogEntryDto } from '../queue-request.model';
import {
    durationBetween,
    executorGroupOf,
    formatDurationMs,
    groupExecutors,
    PhaseSpan,
    phaseSpans
} from '../queue-request.util';
import { QueueLogRowComponent } from '../queue-log-row/queue-log-row.component';
import { ExpandableMessageComponent } from '../expandable-message/expandable-message.component';

/**
 * The delkørsler of one kørsel, grouped by status with errors first.
 *
 * The page is about failures: you open it to see whether a kørsel has stalled or failed, not to
 * confirm that the completed chunks completed. So the failed group — and every chunk inside it —
 * is expanded from the start, and "Færdige" stays collapsed and exists mainly to reach payloads.
 *
 * With `developerInfo` (admins) each chunk also shows when it was picked up, how long it ran, and —
 * when opened — its phase strip: Udfører → Forbereder → Læser data → Synkroniserer data → Færdig with
 * the duration of each. That is the per-delkørsel view of QueueTimeTracker, and the answer to "which
 * part of a slow kørsel was slow".
 */
@Component({
    selector: 'app-executor-accordion',
    templateUrl: './executor-accordion.component.html',
    styleUrl: './executor-accordion.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, DatePipe, QueueStatusLabelPipe, QueueLogRowComponent, ExpandableMessageComponent]
})
export class ExecutorAccordionComponent {
    /** Doubles as the reset key for both override maps below. */
    readonly requestId = input.required<string>();
    readonly executers = input.required<readonly QueueExecuterDto[]>();
    readonly logs = input<readonly QueueLogEntryDto[]>([]);
    /** Show timing detail (pickup time, duration, phase strip). Admin-only; the host decides. */
    readonly developerInfo = input(false);

    readonly downloadPayload = output<QueueExecuterDto>();

    protected readonly groups = computed(() => groupExecutors(this.executers()));

    /** A lone chunk has nothing worth collapsing, so it renders open and without a chevron. */
    protected readonly isSingleExecutor = computed(() => this.executers().length === 1);

    protected readonly logsByExecuter = computed(() => {
        const byExecuter = new Map<string, QueueLogEntryDto[]>();
        for (const entry of this.logs()) {
            if (!entry.executerId) continue;
            const existing = byExecuter.get(entry.executerId);
            if (existing) existing.push(entry);
            else byExecuter.set(entry.executerId, [entry]);
        }
        return byExecuter;
    });

    /**
     * Open by default: errors always, and "kører nu" when there are no errors — so a kørsel still
     * working also shows something without a click. User toggles live in the override maps and win.
     */
    private readonly defaultOpenGroups = computed(() => {
        const groups = this.groups();
        const open = new Set<ExecutorGroupKey>();
        if (groups.some(group => group.key === 'failed')) open.add('failed');
        else if (groups.some(group => group.key === 'running')) open.add('running');
        else if (groups.length === 1) open.add(groups[0].key);
        return open;
    });

    /**
     * Keyed on the request id, not cleared in the click handler: the selection also changes on a deep
     * link, a filter fallback and a poll, and each of those must drop the previous kørsel's state.
     */
    private readonly groupOverrides = linkedSignal<string, ReadonlyMap<ExecutorGroupKey, boolean>>({
        source: () => this.requestId(),
        computation: () => new Map()
    });

    private readonly executerOverrides = linkedSignal<string, ReadonlyMap<string, boolean>>({
        source: () => this.requestId(),
        computation: () => new Map()
    });

    isGroupExpanded(key: ExecutorGroupKey): boolean {
        return this.groupOverrides().get(key) ?? this.defaultOpenGroups().has(key);
    }

    toggleGroup(key: ExecutorGroupKey): void {
        const expanded = this.isGroupExpanded(key);
        this.groupOverrides.update(current => new Map(current).set(key, !expanded));
    }

    /**
     * Whether opening this chunk would show anything.
     *
     * `message` counts as content, not just log rows: for kørsler older than the 2026-09 logging
     * change the message is the only record of a failed chunk — hiding it behind "no logs" would hide
     * the error itself. The phase strip counts too, but only for those who get to see it.
     */
    hasDetails(executer: QueueExecuterDto): boolean {
        return !!executer.message
            || this.logsFor(executer).length > 0
            || (this.developerInfo() && executer.phases.length > 0);
    }

    /**
     * Whether the chunk gets an expand control at all.
     *
     * Two different reasons not to, both ending in a plain row: a lone chunk is already open and
     * has nothing to collapse to, and an empty chunk would open onto nothing. Offering the button
     * anyway reads as broken rather than as empty.
     */
    canToggle(executer: QueueExecuterDto): boolean {
        return this.hasDetails(executer) && !this.isSingleExecutor();
    }

    /**
     * Failed chunks are expanded from the start: opening a failed kørsel must put the errors on
     * screen immediately. Everything else is closed — a completed chunk has nothing to show beyond
     * its payload, which you fetch from the "Færdige" group.
     *
     * An empty chunk is never expanded, whatever the override map says: the map survives nothing
     * but a request change, and a chunk can go from having a message to not having one.
     */
    isExecutorExpanded(executer: QueueExecuterDto): boolean {
        if (!this.hasDetails(executer)) return false;

        const override = this.executerOverrides().get(executer.id);
        if (override !== undefined) return override;
        return this.isSingleExecutor() || executorGroupOf(executer.status) === 'failed';
    }

    toggleExecutor(executer: QueueExecuterDto): void {
        if (!this.canToggle(executer)) return;
        const expanded = this.isExecutorExpanded(executer);
        this.executerOverrides.update(current => new Map(current).set(executer.id, !expanded));
    }

    /**
     * The rollup logs a failed chunk's `Message` verbatim, and since 2026-09 the Synchronize service
     * logs it once more the moment the chunk fails — both are copies of the error already shown above
     * the log rows. Filtered out here rather than dropping the log rows wholesale, so genuinely new
     * lines still appear.
     */
    logsFor(executer: QueueExecuterDto): readonly QueueLogEntryDto[] {
        const rows = this.logsByExecuter().get(executer.id) ?? [];
        return executer.message
            ? rows.filter(entry => entry.message !== executer.message && !entry.message.endsWith(executer.message))
            : rows;
    }

    /** The chunk's phases with durations; the last phase runs until the chunk completed. */
    phasesFor(executer: QueueExecuterDto): PhaseSpan[] {
        return phaseSpans(executer.phases, executer.completedAt);
    }

    /** Pickup-to-completion, or '…' while running. '' when the chunk predates attributed tracker rows. */
    durationLabelFor(executer: QueueExecuterDto): string {
        if (!executer.startedAt) return '';
        if (!executer.completedAt) return '…';
        const ms = durationBetween(executer.startedAt, executer.completedAt);
        return ms === null ? '' : formatDurationMs(ms);
    }
}
